"use client";

import { useContext, useMemo } from "react";

import { useTenant } from "@/contexts/tenant-provider";
import { UserContext } from "@/contexts/user-context";

import { hasConsoleDestination } from "@/lib/menu-access";

const SETTINGS_HOME = "/settings";

/**
 * The tenant-relative path this user should treat as home.
 *
 * Normally the tenant's configured home page. For a user whose role reaches no
 * console page at all, that page is unopenable, so settings becomes home.
 *
 * Scoping to a role needs a resolved user, and the auth gates that reach this
 * through `useNavigateHome` don't have one: the patient portal and the invite
 * page mount no UserProvider at all, and provider login mounts one while
 * nobody is signed in yet. Read the context directly — `useUser()` throws
 * without a provider — and keep the tenant home until a user resolves.
 */
export function useHomeRoute(): string {
  const { tenant } = useTenant();
  const currentUser = useContext(UserContext)?.currentUser;
  const tenantHome = tenant?.homePage || "/dashboard";

  return useMemo(() => {
    if (!currentUser) return tenantHome;
    if (!hasConsoleDestination(currentUser, tenant?.enabledModules)) {
      return SETTINGS_HOME;
    }
    return tenantHome;
  }, [currentUser, tenant?.enabledModules, tenantHome]);
}
